import { useCallback, useState } from 'react';
import { DateFormat, ServerDateFormat, ServerTimeFormat, TimeFormat } from 'utils/constants';
import { courseId } from 'modules/api-requests/course/constants';
import { useCourseRates } from 'modules/api-requests/rates/queries';
import { IUserWaitlist } from 'modules/api-requests/waitlist/types';
import { useForm } from 'antd/es/form/Form';
import { Button, Checkbox, DatePicker, Divider, Form, Radio, TimePicker } from 'antd/es';
import { Calendar, defaultAvailableDays } from 'components/calendar';
import { weekDaysValidator } from 'utils/validators/weekdays-validator';
import notification from 'antd/es/notification';
import { useMutation, useQueryClient } from 'react-query';
import { createUserWaitlist, updateUserWaitlist } from '../../../../modules/api-requests/waitlist/index';
import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import { getProfileState } from 'models/profile/selectors';
import { PhoneFormItem } from 'components/phone';
import { push } from 'connected-react-router';
import { PathCreator, RoutePaths } from 'routing/constants';
import { useAppDispatch, useAppSelector } from 'utils/hooks';
import { profileActions } from 'models/profile';
import momentTimeZone from 'moment-timezone';
import { getCourseSelector } from 'models/course/selectors';

interface IUserWaitlistFormParams {
  data?: IUserWaitlist;
  onSave: () => void;
}

interface IUserWaitlistForm extends IUserWaitlist {
  time: [Moment, Moment];
}

const initialWaitlistForm: Partial<IUserWaitlistForm> = {
  rateIds: [],
  isActive: true,
  weekly: false,
  weekDays: defaultAvailableDays,
};

export const UserWaitlistForm = ({ data: internalData, onSave }: IUserWaitlistFormParams) => {
  const data = internalData?.id
    ? {
        ...internalData,
        weekDays: internalData.weekDays ?? defaultAvailableDays,
        numOfPlayers: Math.min(2, internalData.numOfPlayers),
        date: internalData.date ? moment(internalData.date, ServerDateFormat) : undefined,
        time: [moment(internalData.startTime, ServerTimeFormat), moment(internalData.endTime, ServerTimeFormat)],
      }
    : initialWaitlistForm;

  const [form] = useForm();
  const { data: ratesList } = useCourseRates(courseId);
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const currentCourse = useAppSelector(getCourseSelector);
  const currentProfile = useSelector(getProfileState);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({ ...data });
  const [weekly, setWeekly] = useState(formData.weekly ?? false);
  const [phoneNumber, setPhoneNumber] = useState<string>(currentProfile.phoneNumber ?? '');

  const { mutate: upsertWaitlist, isLoading } = useMutation({
    mutationKey: 'upsertWaitlist',
    mutationFn: (data: IUserWaitlistForm) => {
      const upsertData = {
        ...data,
        courseId,
        weekly,
        date: moment(data.date).format(ServerDateFormat),
        startTime: data.time[0].format(ServerTimeFormat),
        endTime: data.time[1].format(ServerTimeFormat),
        minPrice: null,
        maxPrice: null,
      };
      if (data.id) {
        return updateUserWaitlist(upsertData);
      } else {
        return createUserWaitlist({ ...upsertData, phoneNumber }).then((x) => {
          dispatch(profileActions.setFields({ phoneNumber }));
          dispatch(push(PathCreator[RoutePaths.PROFILE_WAITLIST].getUrl()));
          return x;
        });
      }
    },
    onSuccess: async () => {
      notification.success({ message: 'Waitlist was updated!' });
      queryClient.invalidateQueries('useUserWaitlist');
      onSave();
    },
    onError: (error) => notification.error({ message: String(error) }),
  });

  const handleSave = () => {
    upsertWaitlist({ ...formData } as IUserWaitlistForm);
  };
  const handleChange = (changedValue: Partial<IUserWaitlistForm>, formValues: IUserWaitlistForm) => {
    setFormData({ ...formData, ...formValues });
  };

  const changeStep = useCallback(
    (newStep) => {
      if (newStep > currentStep) {
        form.validateFields().then(() => {
          setCurrentStep(newStep);
        });
      } else {
        setCurrentStep(newStep);
      }
    },
    [currentStep, form]
  );

  const onSaveClick = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(() => Promise.reject('Form validation failed'));
  };

  const disabledDate = useCallback(
    (date: Moment) => {
      const dateTz = date.tz(currentCourse?.timeZone || momentTimeZone.tz.guess());
      const leftDate = moment()
        .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
        .subtract(1, 'days')
        .endOf('day');
      const rightDate = moment()
        .tz(currentCourse?.timeZone || momentTimeZone.tz.guess())
        .add(currentCourse?.waitlistLookupDays ?? 5, 'days')
        .endOf('day');
      return dateTz < leftDate || dateTz > rightDate;
    },
    [currentCourse]
  );

  return (
    <Form preserve form={form} initialValues={formData} onFinish={handleSave} onValuesChange={handleChange}>
      <Form.Item name="id" hidden></Form.Item>
      <Form.Item name="isActive" hidden></Form.Item>
      <Form.Item name="weekly" hidden></Form.Item>

      {currentStep === 1 && (
        <div>
          <div className="waitlist-form-hint">
            We'll send you an email and/or text message notification when the tee time becomes available based on your
            preferences
          </div>
          <Divider />
          <div className="waitlist-form-field">
            <span className="waitlist-form-field-label">How often would you like to be notified?</span>
            <div className="waitlist-form-checkbox-buttons">
              <Button type={weekly ? 'primary' : 'default'} onClick={() => setWeekly(true)}>
                Every Week
              </Button>
              <Button type={!weekly ? 'primary' : 'default'} onClick={() => setWeekly(false)}>
                Only Once
              </Button>
            </div>
          </div>
          <div className="waitlist-form-field">
            <span className="waitlist-form-field-label">
              {weekly ? 'Choose the day(s) of the week you would like to play:' : 'When would you like to play?'}
            </span>
            {weekly ? (
              <Form.Item
                name="weekDays"
                rules={[{ validator: weekDaysValidator, message: 'Select at least one weekday' }]}
              >
                <Calendar size="large" />
              </Form.Item>
            ) : (
              <Form.Item name="date" rules={[{ required: true, message: 'This field is required' }]}>
                <DatePicker inputReadOnly format={DateFormat} disabledDate={disabledDate} />
              </Form.Item>
            )}
          </div>
        </div>
      )}

      {[2, 3, 4, 5].includes(currentStep) && (
        <>
          <div className="waitlist-form-info">
            <span>
              <b>Date / Weekday:</b>{' '}
              {weekly && formData.weekDays
                ? 'Every ' +
                  Object.entries(formData.weekDays)
                    .filter(([_, x]) => !!x)
                    .map(([x, _]) => x)
                    .join(', ')
                : moment(formData.date).format(DateFormat)}
            </span>
            <span hidden={currentStep === 2}>
              <b>Time:</b>{' '}
              {formData.time
                ? [formData.time[0].format(TimeFormat), formData.time[1].format(TimeFormat)].join(' - ')
                : 'n/a'}
            </span>
            <span hidden={currentStep === 3}>
              <b>Players:</b> {formData.numOfPlayers || 'n/a'}
            </span>
            <span hidden={currentStep === 4}>
              <b>Rates:</b>{' '}
              {formData.rateIds && formData.rateIds.length
                ? formData.rateIds.map((x) => ratesList?.find((r) => r.id === x)?.name).join(', ')
                : 'n/a'}
            </span>
          </div>
          <div hidden={currentStep === 5 && !!formData.id}>
            <Divider />
          </div>
        </>
      )}

      {currentStep === 2 && (
        <div className="waitlist-form-field">
          <span className="waitlist-form-field-label">Choose what time range you want to play:</span>
          <Form.Item name="time" rules={[{ required: true, message: 'This field is required' }]}>
            <TimePicker.RangePicker inputReadOnly format={TimeFormat} />
          </Form.Item>
        </div>
      )}

      {currentStep === 3 && (
        <div className="waitlist-form-field">
          <span className="waitlist-form-field-label">How many players?</span>
          <Form.Item name="numOfPlayers" rules={[{ required: true, message: 'Please select the number of players' }]}>
            <Radio.Group className="waitlist-form-radio-buttons" size="large">
              <Radio.Button value={1} key="numOfPlayers-1">
                Single Player
              </Radio.Button>
              <Radio.Button value={2} key="numOfPlayers-2">
                Two or More Players
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </div>
      )}

      {currentStep === 4 && (
        <div className="waitlist-form-field">
          <span className="waitlist-form-field-label">What rate would you like to play at?</span>
          <Form.Item
            name="rateIds"
            valuePropName="value"
            className="waitlist-form-checkbox-group"
            rules={[
              {
                validator: (_, val) => (val.length > 0 ? Promise.resolve() : Promise.reject()),
                message: 'At least one rate is required',
              },
            ]}
          >
            <Checkbox.Group options={ratesList?.map((rate) => ({ label: rate.name, value: rate.id }))} />
          </Form.Item>
        </div>
      )}

      {currentStep === 5 && !formData.id && (
        <div className="waitlist-form-summary">
          <div className="waitlist-form-field">
            <span className="waitlist-form-field-label">
              Please enter your mobile phone number, or ensure it's correct:
            </span>
            <PhoneFormItem
              name="phoneNumber"
              initialValue={phoneNumber}
              inputProps={{
                useRawValue: true,
                onChange: (value) => setPhoneNumber(value),
                country: 'us',
                size: 'large',
                placeholder: 'Phone number',
              }}
            ></PhoneFormItem>
          </div>
          <p>
            <b>Awesome! We're almost done.</b>
          </p>
          <p>
            Make sure to review the information above. This will activate your notifications, keeping you informed when
            your desired tee time is available.
          </p>
        </div>
      )}

      <div className="waitlist-form-buttons">
        <Button type="link" hidden={currentStep === 1} onClick={() => changeStep(currentStep - 1)}>
          Go Back
        </Button>
        <Button hidden={currentStep === 5} type="primary" onClick={() => changeStep(currentStep + 1)}>
          Next Step
        </Button>
        <Button hidden={currentStep !== 5} type="primary" onClick={onSaveClick} loading={isLoading}>
          Save
        </Button>
      </div>
    </Form>
  );
};

